import { useState, useEffect, useCallback } from "react"
import {
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Title } from "components/Common/Title"
import analyticsService from "services/analyticsService"
import type { IAnalyticsExamFailReason } from "types/analyticsTypes"

type Props = {
  orgId: string | null
}

export function ExamFailReasonsSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamFailReason[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    analyticsService
      .getExamsFailReasons(orgId || undefined)
      .then((result) => {
        setData(result)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load fail reason data.")
        setLoading(false)
      })
  }, [orgId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={36} sx={{ mb: 1 }} />
        {Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((key) => (
          <Skeleton key={key} variant="rectangular" width="100%" height={32} sx={{ mb: 0.5 }} />
        ))}
      </Paper>
    )
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>FAIL REASONS SUMMARY</Title>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={fetchData} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Title>FAIL REASONS SUMMARY</Title>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Failed exam counts grouped by fail reason. "Unknown" means no reason was recorded.
      </Typography>

      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No fail reason data available.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Fail Reason</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.failReason}>
                  <TableCell>{row.failReason}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
